import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ClientApiService } from '../../../../clients/services/client-api/ClientApiService';
import { Client } from '../../../../clients/types/Client';
import { EventApiService } from '../../../../events/services/event-api/EventApiService';
import { SportsEvent } from '../../../../events/types/Event';
import { OrderApiService } from '../../../../orders/services/order-api/OrderApiService';
import { Order } from '../../../../orders/types/Order';
import { HttpResponse } from '../../../types/HttpResponse';
import { LayoutModule } from '../../../layout/layout.module';

interface DashboardBar {
  label: string;
  ordersTotal: number;
  revenue: number;
  height: number;
}

interface ClientPerformance {
  id: string;
  routeId: string;
  name: string;
  ordersTotal: number;
  revenue: number;
}

interface EventPerformance {
  id: string;
  routeId: string;
  name: string;
  startsAt: string | null;
  ticketsTotal: number;
  revenue: number;
  plannedProfit: number;
}

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    RouterModule,
  ],
  providers: [
    ClientApiService,
    OrderApiService,
    EventApiService,
  ],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.sass',
})
export class DashboardPageComponent implements OnInit {
  public clients: Client[] = [];
  public clientsTotal = 0;
  public activeClientsTotal = 0;
  public inactiveClientsTotal = 0;
  public orders: Order[] = [];
  public events: SportsEvent[] = [];
  public loading = false;
  public errorMessage: string | null = null;

  public constructor(
    private clientApi: ClientApiService,
    private orderApi: OrderApiService,
    private eventApi: EventApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadDashboardData();
  }

  public get activeClientsPercent(): number {
    return this.clientsTotal > 0 ? Math.round((this.activeClientsTotal / this.clientsTotal) * 100) : 0;
  }

  public get paidOrders(): Order[] {
    return this.orders.filter((order) => order.status === 'paid');
  }

  public get reservedOrdersTotal(): number {
    return this.orders.filter((order) => order.status === 'reserved').length;
  }

  public get paidRevenue(): number {
    return this.paidOrders.reduce((total, order) => total + order.total, 0);
  }

  public get reservedRevenue(): number {
    return this.orders.filter((order) => order.status === 'reserved').reduce((total, order) => total + order.total, 0);
  }

  public get purchaseTotal(): number {
    return this.events.reduce((total, event) => total + event.summary.purchaseTotal, 0);
  }

  public get expenseTotal(): number {
    return this.events.reduce((total, event) => total + event.summary.expenseTotal, 0);
  }

  public get investedTotal(): number {
    return this.purchaseTotal + this.expenseTotal;
  }

  public get plannedProfitTotal(): number {
    return this.events.reduce((total, event) => total + event.summary.plannedProfitAfterExpenses, 0);
  }

  public get soldPurchaseTotal(): number {
    const soldTicketIds = new Set(this.paidOrders.flatMap((order) => order.items.map((item) => item.ticketId)));
    return this.events.reduce((total, event) => total + event.tickets
      .filter((ticket) => soldTicketIds.has(ticket.id))
      .reduce((eventTotal, ticket) => eventTotal + ticket.purchasePrice, 0), 0);
  }

  public get paidEventExpenseTotal(): number {
    const paidEventIds = new Set(this.paidOrders.map((order) => order.eventId));
    return this.events
      .filter((event) => paidEventIds.has(event.id))
      .reduce((total, event) => total + event.summary.expenseTotal, 0);
  }

  public get actualTicketMargin(): number {
    return this.paidRevenue - this.soldPurchaseTotal;
  }

  public get actualProfitAfterExpenses(): number {
    return this.actualTicketMargin - this.paidEventExpenseTotal;
  }

  public get latestOrders(): Order[] {
    return [...this.orders]
      .sort((firstOrder, secondOrder) => new Date(secondOrder.createdAt).getTime() - new Date(firstOrder.createdAt).getTime())
      .slice(0, 5);
  }

  public get latestClients(): Client[] {
    return [...this.clients]
      .sort((firstClient, secondClient) => new Date(secondClient.createdAt).getTime() - new Date(firstClient.createdAt).getTime())
      .slice(0, 5);
  }

  public get orderDynamics(): DashboardBar[] {
    const today = this.stripTime(new Date());
    const days = Array.from({ length: 7 }, (_item, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (6 - index));
      return date;
    });
    const bars = days.map((date) => {
      const dayOrders = this.orders.filter((order) => this.isSameDay(new Date(order.createdAt), date));
      return {
        label: this.formatDayLabel(date),
        ordersTotal: dayOrders.length,
        revenue: dayOrders.filter((order) => order.status === 'paid').reduce((total, order) => total + order.total, 0),
        height: 0,
      };
    });
    const maxOrders = Math.max(...bars.map((bar) => bar.ordersTotal), 1);

    return bars.map((bar) => ({
      ...bar,
      height: Math.max(Math.round((bar.ordersTotal / maxOrders) * 100), 8),
    }));
  }

  public get mostActiveClient(): ClientPerformance | null {
    const clientById = new Map(this.clients.map((client) => [client.id, client]));
    const performanceByClient = new Map<string, ClientPerformance>();
    for (const order of this.paidOrders) {
      const client = clientById.get(order.clientId);
      const current = performanceByClient.get(order.clientId) ?? {
        id: order.clientId,
        routeId: client?.publicIdentifier || order.clientId,
        name: order.client?.name || this.getClientName(client) || 'Клиент',
        ordersTotal: 0,
        revenue: 0,
      };
      current.ordersTotal += 1;
      current.revenue += order.total;
      performanceByClient.set(order.clientId, current);
    }

    return [...performanceByClient.values()]
      .sort((firstClient, secondClient) => secondClient.ordersTotal - firstClient.ordersTotal || secondClient.revenue - firstClient.revenue)[0] ?? null;
  }

  public get bestEvent(): EventPerformance | null {
    const eventById = new Map(this.events.map((event) => [event.id, event]));
    const performanceByEvent = new Map<string, EventPerformance>();
    for (const order of this.paidOrders) {
      const event = eventById.get(order.eventId);
      const current = performanceByEvent.get(order.eventId) ?? {
        id: order.eventId,
        routeId: event?.slug || order.eventId,
        name: order.event?.name || this.getEventName(event) || 'Событие',
        startsAt: order.event?.startsAt || event?.startsAt || null,
        ticketsTotal: 0,
        revenue: 0,
        plannedProfit: event?.summary.plannedProfitAfterExpenses ?? 0,
      };
      current.ticketsTotal += order.items.length;
      current.revenue += order.total;
      performanceByEvent.set(order.eventId, current);
    }

    const byPaidRevenue = [...performanceByEvent.values()]
      .sort((firstEvent, secondEvent) => secondEvent.revenue - firstEvent.revenue || secondEvent.ticketsTotal - firstEvent.ticketsTotal)[0];
    if (byPaidRevenue) {
      return byPaidRevenue;
    }

    const bestPlannedEvent = [...this.events].sort((firstEvent, secondEvent) => secondEvent.summary.plannedProfitAfterExpenses - firstEvent.summary.plannedProfitAfterExpenses)[0];
    return bestPlannedEvent ? {
      id: bestPlannedEvent.id,
      routeId: bestPlannedEvent.slug || bestPlannedEvent.id,
      name: this.getEventName(bestPlannedEvent),
      startsAt: bestPlannedEvent.startsAt,
      ticketsTotal: bestPlannedEvent.summary.ticketsTotal,
      revenue: bestPlannedEvent.summary.plannedRevenue,
      plannedProfit: bestPlannedEvent.summary.plannedProfitAfterExpenses,
    } : null;
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return '—';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  public getClientName(client: Client | undefined): string {
    if (!client) {
      return '';
    }

    return `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || client.telegramUsername || client.telegramId || 'Клиент';
  }

  public getEventName(event: SportsEvent | undefined): string {
    return event ? `${event.homeTeam.name} - ${event.awayTeam.name}` : '';
  }

  public getOrderStatusName(order: Order): string {
    if (order.status === 'paid') {
      return 'Оплачен';
    }
    if (order.status === 'reserved') {
      return 'Резерв';
    }
    if (order.status === 'cancelled') {
      return 'Отменен';
    }
    if (order.status === 'expired') {
      return 'Истек';
    }

    return order.status;
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public trackByBar(_index: number, item: DashboardBar): string {
    return item.label;
  }

  private async loadDashboardData(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const [
        clientsResponse,
        activeClientsResponse,
        inactiveClientsResponse,
        ordersResponse,
        eventsResponse,
      ] = await Promise.all([
        this.clientApi.getClients({
          search: '',
          status: '',
          page: 1,
          pageSize: 200,
        }),
        this.clientApi.getClients({
          search: '',
          status: 'active',
          page: 1,
          pageSize: 1,
        }),
        this.clientApi.getClients({
          search: '',
          status: 'inactive',
          page: 1,
          pageSize: 1,
        }),
        this.orderApi.getOrders(),
        this.eventApi.getEvents(),
      ]);

      const clientsResult = this.unwrapResponse(clientsResponse);
      const activeClientsResult = this.unwrapResponse(activeClientsResponse);
      const inactiveClientsResult = this.unwrapResponse(inactiveClientsResponse);
      this.clients = clientsResult?.clients ?? [];
      this.clientsTotal = clientsResult?.total ?? 0;
      this.activeClientsTotal = activeClientsResult?.total ?? this.clients.filter((client) => client.status === 'active').length;
      this.inactiveClientsTotal = inactiveClientsResult?.total ?? this.clients.filter((client) => client.status === 'inactive').length;
      this.orders = this.unwrapResponse(ordersResponse) ?? [];
      this.events = this.unwrapResponse(eventsResponse) ?? [];
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить аналитику.';
      this.clients = [];
      this.orders = [];
      this.events = [];
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private stripTime(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    return normalizedDate;
  }

  private isSameDay(firstDate: Date, secondDate: Date): boolean {
    return this.stripTime(firstDate).getTime() === this.stripTime(secondDate).getTime();
  }

  private formatDayLabel(date: Date): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
    }).format(date);
  }
}
